// ============================================
// HISTORIQUE D'UN DOSSIER — libellés lisibles
// ============================================
// Les actions viennent de l'enum `TicketAction` (backend, prisma/schema.prisma).
// Les valeurs avant/après sont des codes (statut, priorité) ou des
// identifiants (affectation) : on les traduit, jamais d'UUID à l'écran.

import { STATUS_LABELS, PRIORITY_LABELS, ISSUE_TYPE_LABELS } from '../../shared/labels';
import type { IssueType, TicketHistory, TicketPriority, TicketStatus } from '../../types';

export const LIBELLES_ACTION: Record<string, string> = {
  CREATED: 'Dossier créé',
  UPDATED: 'Dossier modifié',
  ASSIGNED: 'Affectation modifiée',
  UNASSIGNED: 'Dossier désaffecté',
  AUTO_ASSIGNED: 'Affectation automatique',
  TRANSFER_ACCEPTED: 'Transfert accepté',
  TRANSFER_DECLINED: 'Transfert refusé',
  STATUS_CHANGED: 'Statut modifié',
  PRIORITY_CHANGED: 'Priorité modifiée',
  ESCALATED: 'Dossier escaladé',
  REOPENED: 'Dossier rouvert',
  CLOSED: 'Dossier clos',
  NOTE_ADDED: 'Note interne ajoutée',
  RELANCE: 'Relance envoyée au client',
  CLOTURE_AUTO: 'Clôture automatique',
  FUSION: 'Fusion de dossiers',
  APPEL: 'Appel téléphonique',
};

export const libelleAction = (action: string): string => LIBELLES_ACTION[action] ?? 'Autre action';

const UUID_OU_CUID = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|c[a-z0-9]{20,30})$/i;

const statut = (v?: string | null) => (v ? STATUS_LABELS[v as TicketStatus] ?? v : '—');
const priorite = (v?: string | null) => (v ? PRIORITY_LABELS[v as TicketPriority] ?? v : '—');
const typeDemande = (v?: string | null) => (v ? ISSUE_TYPE_LABELS[v as IssueType] ?? v : '—');

/**
 * Une ligne lisible pour le détail de l'entrée, ou null s'il n'y a rien à dire.
 * `nomUtilisateur` traduit un identifiant de compte en nom.
 */
export function decrireChangement(
  h: Pick<TicketHistory, 'action' | 'field' | 'oldValue' | 'newValue' | 'metadata'>,
  nomUtilisateur: (id: string) => string | undefined,
): string | null {
  const personne = (id?: string | null) => (id ? nomUtilisateur(id) ?? 'compte supprimé ou inconnu' : 'personne');
  const meta = (h.metadata && typeof h.metadata === 'object') ? h.metadata as Record<string, unknown> : {};
  const raison = typeof meta.raison === 'string' ? meta.raison : null;

  let ligne: string | null = null;
  switch (h.field) {
    case 'status':
      ligne = h.oldValue ? `${statut(h.oldValue)} → ${statut(h.newValue)}` : `Statut : ${statut(h.newValue)}`;
      break;
    case 'priority':
      ligne = `${priorite(h.oldValue)} → ${priorite(h.newValue)}`;
      break;
    case 'issueType':
      ligne = `Type : ${typeDemande(h.oldValue)} → ${typeDemande(h.newValue)}`;
      break;
    case 'assignedTo':
    case 'assignedToId':
      ligne = h.oldValue
        ? `${personne(h.oldValue)} → ${h.newValue ? personne(h.newValue) : 'non assigné'}`
        : `Affecté à ${personne(h.newValue)}`;
      break;
    case 'slaBreached':
      ligne = h.newValue === 'true' ? 'Délai SLA dépassé' : 'Délai SLA de nouveau respecté';
      break;
    case 'spamPossible':
      ligne = h.newValue === 'false' ? 'Ce n\'est pas un spam : marque « spam possible » retirée' : 'Marqué « spam possible »';
      break;
    case 'rattachementDomaine':
      ligne = h.newValue === 'CONFIRME'
        ? 'Société identifiée par le domaine : confirmée'
        : h.newValue === 'A_CONFIRMER' || h.newValue === 'PROPOSE'
          ? 'Rattaché à une société par le domaine de l\'expéditeur (à confirmer)'
          : 'Rattachement par le domaine refusé : ce n\'est pas cette société';
      break;
    case 'horsSav':
      ligne = 'Pas une demande SAV : clos sans notification au client';
      break;
    case 'source':
      ligne = `Le dossier ${h.oldValue ?? '—'} a été fusionné dans celui-ci`;
      break;
    case 'cible':
      ligne = `Fusionné dans le dossier ${h.newValue ?? '—'}`;
      break;
    default:
      if (h.field) {
        const lisible = (v?: string | null) => (!v ? '—' : UUID_OU_CUID.test(v) ? (nomUtilisateur(v) ?? 'élément') : v);
        ligne = `${h.field} : ${lisible(h.oldValue)} → ${lisible(h.newValue)}`;
      }
  }

  if (h.action === 'RELANCE' && typeof meta.numero === 'number') {
    ligne = `Relance n° ${meta.numero}`;
  }
  if (raison) ligne = ligne ? `${ligne} (${raison})` : raison;
  return ligne;
}
